import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  FileEdit, Clock, CheckCircle2, XCircle, Loader2, Mail,
  Building2, ArrowRight, ChevronRight, Calendar,
} from "lucide-react";

/**
 * Profile change requests from existing advertisers (round 8, decision A4).
 *
 * These are NOT new-advertiser applications, which is what the old page's place in the
 * nav implied. Each one carries an account_id and a set of changed fields, so it belongs
 * beside the advertiser it changes rather than in a queue of its own.
 *
 * The diff view is lifted from pages/AdminAccountRequests.jsx rather than rebuilt: it
 * already reads well, and two renderings of the same before-and-after would drift.
 *
 * The PARENT owns the request list, because the same array drives both this panel and
 * the flag on an advertiser row. `selected` is lifted for the same reason: clicking a
 * row's flag has to open that request in here.
 */
export function statusBadge(status) {
  switch (status) {
    case "PENDING":
      return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    case "APPROVED":
      return <Badge className="bg-green-500/10 text-green-500 border-green-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Approved</Badge>;
    case "REJECTED":
      return <Badge className="bg-red-500/10 text-red-500 border-red-500/30"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

const formatDate = (value) => {
  if (!value) return "Not recorded";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

const renderValue = (value) => {
  if (value === null || value === undefined || value === "") return "Not set";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

export default function PendingChangesPanel({ requests, selected, onSelect, onReviewed }) {
  const { token } = useAuth();
  const [adminNotes, setAdminNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const open = (request) => {
    setAdminNotes("");
    onSelect(request);
  };

  const review = async (status) => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/account-requests/${selected.id}/review`,
        { status, admin_notes: adminNotes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        status === "APPROVED" ? "Changes applied to the account" : "Request rejected"
      );
      onSelect(null);
      setAdminNotes("");
      // The advertiser list is refetched too, not just the requests: an approval writes
      // straight to the account document, so the row behind this dialog is now stale.
      await onReviewed?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process the review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {requests.length === 0 ? (
            <div className="p-12 text-center">
              <FileEdit className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">Nothing waiting</h3>
              <p className="text-muted-foreground">
                Profile changes advertisers submit for approval land here.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {requests.map((request) => (
                <div
                  key={request.id}
                  className="p-4 hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => open(request)}
                  data-testid={`pending-change-${request.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Building2 className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold">{request.business_name}</h3>
                        {statusBadge(request.status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {request.requested_by_email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(request.created_at)}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {Object.keys(request.changes || {}).map((field) => (
                          <Badge key={field} variant="outline" className="text-xs">
                            {field.replace(/_/g, " ")}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(isOpen) => !isOpen && onSelect(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="h-5 w-5" />
              Profile change request
            </DialogTitle>
            <DialogDescription>
              Approving applies these changes to {selected?.business_name} immediately.
            </DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="space-y-6">
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Business</span>
                    <p className="font-medium">{selected.business_name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Requested by</span>
                    <p className="font-medium">{selected.requested_by_email}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Submitted</span>
                    <p className="font-medium">{formatDate(selected.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status</span>
                    <div className="mt-1">{statusBadge(selected.status)}</div>
                  </div>
                </div>
                {selected.reason && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <span className="text-muted-foreground text-sm">Reason given</span>
                    <p className="mt-1">{selected.reason}</p>
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-semibold mb-3">Requested changes</h4>
                <div className="space-y-3">
                  {Object.entries(selected.changes || {}).map(([field, newValue]) => (
                    <div key={field} className="p-3 rounded-lg border border-border">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                        {field.replace(/_/g, " ")}
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 p-2 rounded bg-red-500/5 border border-red-500/20">
                          <p className="text-sm text-red-400 whitespace-pre-wrap break-words">
                            {renderValue(selected.current_values?.[field])}
                          </p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 p-2 rounded bg-green-500/5 border border-green-500/20">
                          <p className="text-sm text-green-400 whitespace-pre-wrap break-words">
                            {renderValue(newValue)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selected.status === "PENDING" ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium" htmlFor="admin-notes">
                      Notes (optional)
                    </label>
                    <Textarea
                      spellCheck="true"
                      id="admin-notes"
                      placeholder="Recorded against the decision and sent to the requester."
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={3}
                      className="mt-2"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => review("APPROVED")}
                      disabled={submitting}
                      data-testid="approve-change-request"
                    >
                      {submitting
                        ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        : <CheckCircle2 className="mr-2 h-4 w-4" />}
                      Approve changes
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={() => review("REJECTED")}
                      disabled={submitting}
                      data-testid="reject-change-request"
                    >
                      {submitting
                        ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        : <XCircle className="mr-2 h-4 w-4" />}
                      Reject
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-muted/30 border border-border text-sm text-muted-foreground">
                  <p><strong>Reviewed by:</strong> {selected.reviewed_by || "Unknown"}</p>
                  <p><strong>Reviewed at:</strong> {formatDate(selected.reviewed_at)}</p>
                  {selected.admin_notes && (
                    <p className="mt-2 text-foreground"><strong>Notes:</strong> {selected.admin_notes}</p>
                  )}
                </div>
              )}

              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.open(`mailto:${selected.requested_by_email}?subject=Re: Account change request - ${selected.business_name}`)}
              >
                <Mail className="mr-2 h-4 w-4" />
                Contact {selected.requested_by_email}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
